import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { BookOpen, ConciergeBell, Map, Megaphone, TrendingUp, Send, Pencil } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const NAV_ICONS = [BookOpen, ConciergeBell, Map, Megaphone, TrendingUp];
const BARS = [38, 62, 44, 78, 52, 88, 66];

const Toggle = () => (
  <span className="flex h-5 w-9 items-center rounded-full bg-ostelo-green px-0.5">
    <span className="ml-auto h-4 w-4 rounded-full bg-white shadow" />
  </span>
);

export const DashboardMockup = ({ testId = "dashboard-mockup" }) => {
  const { t } = useTranslation();
  const nav = t("home.dash_nav", { returnObjects: true });
  const rows = t("home.dash_rows", { returnObjects: true });

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: EASE }}
      className="overflow-hidden rounded-2xl border border-ostelo-deep/10 bg-white shadow-[0_30px_70px_-20px_rgb(12,27,71,0.25)]"
      data-testid={testId}
    >
      {/* Barre navigateur */}
      <div className="flex items-center gap-2 border-b border-ostelo-deep/6 bg-[#F6F9FF] px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-ostelo-rose" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FFE9C2]" />
        <span className="h-2.5 w-2.5 rounded-full bg-ostelo-mint" />
        <span className="ml-3 flex-1 truncate rounded-md bg-white px-3 py-1 text-[10px] font-semibold text-ostelo-ink/40">
          app.ostelo.fr/admin
        </span>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="hidden w-40 shrink-0 flex-col gap-1 border-r border-ostelo-deep/6 bg-[#FAFCFF] p-3 sm:flex">
          <p className="px-2 pb-2 font-sans text-sm font-extrabold lowercase tracking-tight text-ostelo-deep">
            ostelo<span className="text-ostelo-orange">.</span>
          </p>
          {nav.map((item, i) => {
            const Icon = NAV_ICONS[i];
            return (
              <div
                key={item}
                className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] font-semibold ${
                  i === 0 ? "bg-ostelo-sky text-ostelo-blue" : "text-ostelo-ink/50"
                }`}
              >
                <Icon size={13} />
                <span className="truncate">{item}</span>
              </div>
            );
          })}
        </div>

        {/* Contenu principal */}
        <div className="min-w-0 flex-1 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-sans text-base font-extrabold text-ostelo-deep">{t("home.dash_title")}</p>
              <p className="text-[11px] text-ostelo-ink/45">{t("home.dash_hotel")}</p>
            </div>
            <span className="rounded-full bg-ostelo-rose px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-ostelo-deep/60">
              {t("home.dash_badge")}
            </span>
          </div>

          {/* Lignes de services avec toggles */}
          <div className="mt-4 space-y-2">
            {rows.map((r, i) => (
              <motion.div
                key={r}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.12, ease: EASE }}
                className="flex items-center justify-between rounded-lg border border-ostelo-deep/6 bg-white px-3 py-2.5"
              >
                <span className="flex items-center gap-2 text-[11px] font-semibold text-ostelo-deep">
                  <Pencil size={11} className="text-ostelo-ink/30" />
                  {r}
                </span>
                <Toggle />
              </motion.div>
            ))}
          </div>

          {/* Publication instantanée */}
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-dashed border-ostelo-blue/40 bg-ostelo-sky/60 px-3 py-2.5">
            <span className="truncate text-[11px] font-medium text-ostelo-ink/50">{t("home.dash_publish")}</span>
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ostelo-blue">
              <Send size={11} className="text-white" />
            </span>
          </div>

          {/* Mini graphique qualitatif */}
          <div className="mt-4 rounded-lg border border-ostelo-deep/6 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ostelo-ink/40">{t("home.dash_chart")}</p>
            <div className="mt-2.5 flex h-16 items-end gap-1.5">
              {BARS.map((h, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.4 + i * 0.07, ease: EASE }}
                  className={`flex-1 rounded-t ${i === BARS.length - 2 ? "bg-ostelo-orange" : "bg-ostelo-blue/75"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
